// src/components/BarcodeScannerModal.tsx
// カメラでISBNバーコード（EAN-13）を読み取るモーダル。
// @zxing/library でカメラ映像から連続的にバーコードをデコードする。
// 日本の書籍には「ISBN」バーコードの下にもう一つ「価格・分類コード」の
// バーコード（19/191始まり）が並んでいることが多く、誤読を避けるため
// 19始まりのコードは無視して「上側のバーコードを読み取ってください」と案内する
// （モバイル版と同じ挙動）。
//
// レイアウト: 画面左半分にカメラ映像、右半分に読み取り結果を大きな文字で表示する
// 2カラム構成。PCのWebカメラに本を近づけると、本自体で画面（特にカメラ映像の中）が
// 覆われてしまい、映像内に小さく出す通知が見えなくなるため、映像と結果表示の
// 領域を分離している。

import React, { useEffect, useState } from 'react';
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat, NotFoundException } from '@zxing/library';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloseIcon from '@mui/icons-material/Close';
import { useAppTokens } from '../context/ThemeModeContext';

export interface ScanResult {
  type: 'success' | 'error' | 'info' | 'pending';
  isbn: string;
  title?: string;
  message: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** バーコードを検出した時に呼ばれる */
  onDetect: (isbn: string) => void;
  /** 検出後、次のスキャンを再開するまで一時停止するためのフラグ（重複確認ダイアログ表示中など） */
  paused: boolean;
  /** 直近の読み取り結果。右側パネルに大きく表示する */
  lastResult: ScanResult | null;
}

const BarcodeScannerModal: React.FC<Props> = ({ open, onClose, onDetect, paused, lastResult }) => {
  const TOKENS = useAppTokens();
  // video要素は MUI Dialog の Fade トランジションの都合で、open が true になった
  // 直後の useEffect ではまだ DOM に存在しないことがある（1テンポ遅れてマウントされる）。
  // ref ではなく state で持つことで、実際に要素がアタッチされたタイミングで
  // 確実に effect を再実行できるようにする（コールバックref パターン）。
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [readerInst, setReaderInst] = useState<BrowserMultiFormatReader | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hintMessage, setHintMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !videoEl) return;
    let stream: MediaStream | null = null;
    let cancelled = false;
    let lastCode: { code: string; at: number } | null = null;

    const start = async () => {
      const video = videoEl;

      if (!navigator.mediaDevices?.getUserMedia) {
        setError('このブラウザではカメラを利用できません。');
        return;
      }

      // React の muted 属性(JSX)はDOMプロパティに反映されないことがあり、
      // それが原因でブラウザの自動再生ポリシーにより video.play() が無音で失敗し
      // 「映像が真っ黒」になることがある。ref経由でプロパティを直接設定して回避する。
      video.muted = true;
      video.setAttribute('muted', 'true');
      video.playsInline = true;
      video.autoplay = true;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            // 解像度を高めに要求し、バーコードを遠ざけても読み取りやすくする
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
      } catch (err) {
        console.error('getUserMedia error:', err);
        if (!cancelled) setError('カメラを起動できませんでした。ブラウザのカメラ権限を確認してください。');
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      video.srcObject = stream;
      try {
        await video.play();
      } catch (err) {
        console.error('video.play() error:', err);
        if (!cancelled) setError('カメラ映像を再生できませんでした。ページを再読み込みして再度お試しください。');
        return;
      }
      setError(null);

      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13]);
      const reader = new BrowserMultiFormatReader(hints);
      setReaderInst(reader);

      try {
        await reader.decodeFromStream(stream, video, (result, err) => {
          if (paused) return;
          if (result) {
            const code = result.getText();
            // 同じコードの連続検出を1.5秒はデバウンス（DetectionSpeed.noDuplicates相当）
            const now = Date.now();
            if (lastCode && lastCode.code === code && now - lastCode.at < 1500) {
              return;
            }
            lastCode = { code, at: now };

            if (code.startsWith('19')) {
              setHintMessage('上側のバーコードを読み取ってください');
              window.setTimeout(() => setHintMessage(null), 2000);
              return;
            }
            onDetect(code);
          } else if (err && !(err instanceof NotFoundException)) {
            // NotFoundException は「今のフレームには写っていない」だけなので無視
            console.error('Barcode scan error:', err);
          }
        });
      } catch (err) {
        console.error('decodeFromStream error:', err);
        if (!cancelled) setError('バーコード読み取りの初期化に失敗しました。');
      }
    };

    start().catch((err) => {
      console.error('start() unhandled error:', err);
      if (!cancelled) setError('予期しないエラーが発生しました。');
    });

    return () => {
      cancelled = true;
      setReaderInst((r) => {
        r?.reset();
        return null;
      });
      stream?.getTracks().forEach((t) => t.stop());
      if (videoEl) videoEl.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoEl]);

  const handleClose = () => {
    readerInst?.reset();
    onClose();
  };

  const resultVisual = (() => {
    if (!lastResult) {
      return {
        icon: <QrCodeScannerIcon sx={{ fontSize: 64, color: TOKENS.textSub }} />,
        color: TOKENS.textSub,
        headline: '本をカメラにかざしてください',
      };
    }
    switch (lastResult.type) {
      case 'success':
        return { icon: <CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32' }} />, color: '#2e7d32', headline: '登録しました' };
      case 'error':
        return { icon: <ErrorIcon sx={{ fontSize: 64, color: TOKENS.error }} />, color: TOKENS.error, headline: '登録に失敗しました' };
      case 'pending':
        return { icon: <CircularProgress size={56} sx={{ color: TOKENS.textSub }} />, color: TOKENS.textSub, headline: '確認中です' };
      default:
        return { icon: <InfoIcon sx={{ fontSize: 64, color: TOKENS.textSub }} />, color: TOKENS.textSub, headline: '検出しました' };
    }
  })();

  return (
    <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { background: TOKENS.bg } }}>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* 左半分: カメラ映像 */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', md: '50%' },
            height: { xs: '42%', md: '100%' },
            background: '#000',
            flexShrink: 0,
          }}
        >
          <video
            ref={setVideoEl}
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
            muted
            playsInline
          />

          {/* スキャン枠オーバーレイ */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '78%',
              aspectRatio: '1 / 0.55',
              border: '2px solid rgba(255,255,255,0.85)',
              borderRadius: 2,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
              pointerEvents: 'none',
            }}
          />

          <Typography
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              color: '#fff',
              fontSize: 12.5,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            バーコードを枠内にかざしてください
          </Typography>

          {error && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(90%, 380px)',
                background: TOKENS.glassStrong,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 2,
                p: 2.5,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: TOKENS.error, fontSize: 13 }}>{error}</Typography>
            </Box>
          )}
        </Box>

        {/* 右半分: 読み取り結果を大きく表示 */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: '58%', md: '100%' },
            background: TOKENS.bg,
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.text }}>バーコードで追加</Typography>
            <Button
              onClick={handleClose}
              startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: TOKENS.bg,
                background: TOKENS.text,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2,
                py: 0.75,
                '&:hover': { background: TOKENS.textSub },
              }}
            >
              終了する
            </Button>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 2,
            }}
          >
            {hintMessage ? (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: '#e0a000' }} />
                <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: '#e0a000' }}>
                  {hintMessage}
                </Typography>
              </>
            ) : (
              <>
                {resultVisual.icon}
                <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: resultVisual.color }}>
                  {resultVisual.headline}
                </Typography>
                {lastResult && (
                  <>
                    {lastResult.title && (
                      <Typography sx={{ fontSize: 18, color: TOKENS.text }}>{lastResult.title}</Typography>
                    )}
                    <Typography sx={{ fontSize: 14, color: TOKENS.textSub }}>{lastResult.message}</Typography>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub, fontFamily: 'monospace' }}>
                      ISBN: {lastResult.isbn}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BarcodeScannerModal;
